import type { FC, FormEvent, ReactNode } from 'react'
import type { DemoRequestErrors, DemoRequestField, DemoRequestPayload } from '@/constants/demo-request'

import Link from 'next/link'
import Image from 'next/image'
import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button'
import SectionHeading from '@/components/landing/section-heading'

import {
    ArrowRight,
    Check,
    ChevronDown,
    CircleAlert,
    CircleCheck,
    LoaderCircle
} from 'lucide-react'

import {
    demoRequestLimits,
    interestOptions,
    portfolioOptions,
    roleOptions,
    validateDemoRequest
} from '@/constants/demo-request'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const emptyForm: DemoRequestPayload = {
    fullName: '',
    workEmail: '',
    company: '',
    role: '',
    portfolioSize: '',
    interests: [],
    message: ''
}

const checklist = [
    'A live forecast for a site like yours',
    'Storage and trading scenarios for next week',
    'A rollout plan with timing and pricing'
]

const fieldOrder: DemoRequestField[] = ['fullName', 'workEmail', 'company', 'role', 'portfolioSize', 'interests', 'message']

const inputClasses = 'h-11 w-full rounded-xl border border-input bg-white px-3.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15 aria-[invalid=true]:border-orange-600 aria-[invalid=true]:focus:ring-orange-600/15'

interface FieldErrorProps {
    id: string
    message?: string
}

const FieldError: FC<FieldErrorProps> = ({ id, message }): ReactNode => {
    if (!message) return null

    return (
        <p id={id} className='mt-1.5 flex items-center gap-1.5 text-xs text-orange-700'>
            <CircleAlert className='size-3.5 shrink-0' aria-hidden='true' />
            {message}
        </p>
    )
}

const DemoRequestSection: FC = (): ReactNode => {
    const [form, setForm] = useState<DemoRequestPayload>(emptyForm)
    const [errors, setErrors] = useState<DemoRequestErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [submittedName, setSubmittedName] = useState('')
    const [submittedEmail, setSubmittedEmail] = useState('')

    const formRef = useRef<HTMLFormElement>(null)
    const honeypotRef = useRef<HTMLInputElement>(null)
    const successRef = useRef<HTMLDivElement>(null)

    const update = <K extends keyof DemoRequestPayload>(key: K, value: DemoRequestPayload[K]): void => {
        setForm((current) => ({ ...current, [key]: value }))

        const field = key as DemoRequestField

        if (errors[field]) {
            setErrors((current) => ({ ...current, [field]: undefined }))
        }
    }

    const toggleInterest = (interest: string): void => {
        update(
            'interests',
            form.interests.includes(interest)
                ? form.interests.filter((item) => item !== interest)
                : [...form.interests, interest]
        )
    }

    const focusFirstError = (nextErrors: DemoRequestErrors): void => {
        const first = fieldOrder.find((field) => nextErrors[field])

        if (!first) return

        const target = formRef.current?.querySelector<HTMLElement>(`[data-field='${first}']`)

        target?.focus()
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const payload: DemoRequestPayload = {
            ...form,
            fullName: form.fullName.trim(),
            workEmail: form.workEmail.trim(),
            company: form.company.trim(),
            message: form.message.trim(),
            website: honeypotRef.current?.value ?? ''
        }

        const nextErrors = validateDemoRequest(payload)

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            setStatus('idle')
            focusFirstError(nextErrors)

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/demo-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            const result = (await response.json().catch(() => ({}))) as { message?: string, errors?: DemoRequestErrors }

            if (!response.ok) {
                if (result.errors && Object.keys(result.errors).length > 0) {
                    setErrors(result.errors)
                    focusFirstError(result.errors)
                }

                setServerMessage(result.message || 'Something went wrong on our side. Please try again in a moment.')
                setStatus('error')

                return
            }

            setSubmittedName(payload.fullName.split(' ')[0] || payload.fullName)
            setSubmittedEmail(payload.workEmail)
            setForm(emptyForm)
            setErrors({})
            setStatus('success')

            window.requestAnimationFrame(() => successRef.current?.focus())
        }
        catch {
            setServerMessage('We could not reach the server. Please check your connection and try again.')
            setStatus('error')
        }
    }

    const reset = (): void => {
        setStatus('idle')
        setServerMessage('')
    }

    return (
        <section
            id='get-started'
            aria-labelledby='get-started-title'
            className='scroll-mt-20 bg-background py-24 sm:py-32'
        >
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='relative isolate overflow-hidden rounded-[2.5rem] bg-forest'>
                    <Image
                        src='/images/hero-turbine.jpg'
                        alt=''
                        fill
                        sizes='100vw'
                        className='-z-20 object-cover object-[50%_65%] opacity-50'
                    />

                    <div aria-hidden='true' className='absolute inset-0 -z-10 bg-gradient-to-b from-forest via-forest/90 to-forest/60 lg:bg-gradient-to-r lg:from-forest lg:via-forest/85 lg:to-forest/30' />

                    <div className='grid gap-10 p-6 sm:p-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14 lg:p-14'>
                        <div className='flex flex-col'>
                            <SectionHeading
                                id='get-started-title'
                                eyebrow='Book a demo'
                                tone='dark'
                                title='See your own portfolio forecast in 30 minutes.'
                                description='Share a few details and a grid specialist will walk you through Gridora using data from a site like yours.'
                            />

                            <ul className='mt-8 space-y-3'>
                                {checklist.map((item) => (
                                    <li key={item} className='flex items-center gap-3 text-[15px] text-white'>
                                        <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-leaf text-forest'>
                                            <Check className='size-3.5' aria-hidden='true' />
                                        </span>
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            <p className='mt-auto pt-10 text-sm text-forest-foreground/65'>
                                No commitment. Most demos are booked within two working days.
                            </p>
                        </div>

                        <div className='rounded-[1.75rem] bg-white p-6 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.6)] sm:p-8'>
                            {status === 'success' ? (
                                <div
                                    ref={successRef}
                                    tabIndex={-1}
                                    role='status'
                                    className='flex min-h-[28rem] flex-col items-center justify-center text-center focus:outline-none'
                                >
                                    <span className='flex size-14 items-center justify-center rounded-full bg-accent text-primary'>
                                        <CircleCheck className='size-7' aria-hidden='true' />
                                    </span>

                                    <h3 className='mt-6 font-heading text-2xl font-light tracking-tight text-foreground sm:text-3xl'>
                                        Thanks, {submittedName}. Your request is in.
                                    </h3>

                                    <p className='mt-3 max-w-sm text-[15px] leading-relaxed text-muted-foreground'>
                                        A grid specialist will reply to <span className='font-medium text-foreground'>{submittedEmail}</span> within one business day with a few times to choose from.
                                    </p>

                                    <button
                                        type='button'
                                        onClick={reset}
                                        className={buttonClasses({ variant: 'outline', className: 'mt-8' })}
                                    >
                                        Send another request
                                    </button>
                                </div>
                            ) : (
                                <form ref={formRef} noValidate onSubmit={onSubmit} aria-describedby='demo-form-note'>
                                    <h3 className='text-lg font-medium text-foreground'>
                                        Request your demo
                                    </h3>

                                    <p id='demo-form-note' className='mt-1 text-sm text-muted-foreground'>
                                        Fields marked with an asterisk are required.
                                    </p>

                                    <div className='mt-6 grid gap-4 sm:grid-cols-2'>
                                        <div>
                                            <label htmlFor='demo-full-name' className='text-sm font-medium text-foreground'>
                                                Full name <span aria-hidden='true' className='text-primary'>*</span>
                                            </label>

                                            <input
                                                id='demo-full-name'
                                                data-field='fullName'
                                                type='text'
                                                autoComplete='name'
                                                required
                                                maxLength={demoRequestLimits.fullName}
                                                value={form.fullName}
                                                onChange={(event) => update('fullName', event.target.value)}
                                                aria-invalid={Boolean(errors.fullName)}
                                                aria-describedby={errors.fullName ? 'demo-full-name-error' : undefined}
                                                placeholder='Maren Holt'
                                                className={cn(inputClasses, 'mt-1.5')}
                                            />

                                            <FieldError id='demo-full-name-error' message={errors.fullName} />
                                        </div>

                                        <div>
                                            <label htmlFor='demo-work-email' className='text-sm font-medium text-foreground'>
                                                Work email <span aria-hidden='true' className='text-primary'>*</span>
                                            </label>

                                            <input
                                                id='demo-work-email'
                                                data-field='workEmail'
                                                type='email'
                                                autoComplete='email'
                                                required
                                                maxLength={demoRequestLimits.workEmail}
                                                value={form.workEmail}
                                                onChange={(event) => update('workEmail', event.target.value)}
                                                aria-invalid={Boolean(errors.workEmail)}
                                                aria-describedby={errors.workEmail ? 'demo-work-email-error' : undefined}
                                                placeholder='maren@company.com'
                                                className={cn(inputClasses, 'mt-1.5')}
                                            />

                                            <FieldError id='demo-work-email-error' message={errors.workEmail} />
                                        </div>

                                        <div>
                                            <label htmlFor='demo-company' className='text-sm font-medium text-foreground'>
                                                Company <span aria-hidden='true' className='text-primary'>*</span>
                                            </label>

                                            <input
                                                id='demo-company'
                                                data-field='company'
                                                type='text'
                                                autoComplete='organization'
                                                required
                                                maxLength={demoRequestLimits.company}
                                                value={form.company}
                                                onChange={(event) => update('company', event.target.value)}
                                                aria-invalid={Boolean(errors.company)}
                                                aria-describedby={errors.company ? 'demo-company-error' : undefined}
                                                placeholder='Northvale Energy'
                                                className={cn(inputClasses, 'mt-1.5')}
                                            />

                                            <FieldError id='demo-company-error' message={errors.company} />
                                        </div>

                                        <div>
                                            <label htmlFor='demo-role' className='text-sm font-medium text-foreground'>
                                                Your role <span className='font-normal text-muted-foreground'>(optional)</span>
                                            </label>

                                            <div className='relative mt-1.5'>
                                                <select
                                                    id='demo-role'
                                                    data-field='role'
                                                    value={form.role}
                                                    onChange={(event) => update('role', event.target.value)}
                                                    aria-invalid={Boolean(errors.role)}
                                                    aria-describedby={errors.role ? 'demo-role-error' : undefined}
                                                    className={cn(inputClasses, 'appearance-none pr-10', !form.role && 'text-muted-foreground')}
                                                >
                                                    <option value=''>Choose a role</option>
                                                    {roleOptions.map((option) => (
                                                        <option key={option} value={option}>{option}</option>
                                                    ))}
                                                </select>

                                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                                            </div>

                                            <FieldError id='demo-role-error' message={errors.role} />
                                        </div>

                                        <div className='sm:col-span-2'>
                                            <label htmlFor='demo-portfolio' className='text-sm font-medium text-foreground'>
                                                Portfolio size <span className='font-normal text-muted-foreground'>(optional)</span>
                                            </label>

                                            <div className='relative mt-1.5'>
                                                <select
                                                    id='demo-portfolio'
                                                    data-field='portfolioSize'
                                                    value={form.portfolioSize}
                                                    onChange={(event) => update('portfolioSize', event.target.value)}
                                                    aria-invalid={Boolean(errors.portfolioSize)}
                                                    aria-describedby={errors.portfolioSize ? 'demo-portfolio-error' : undefined}
                                                    className={cn(inputClasses, 'appearance-none pr-10', !form.portfolioSize && 'text-muted-foreground')}
                                                >
                                                    <option value=''>Choose your installed capacity</option>
                                                    {portfolioOptions.map((option) => (
                                                        <option key={option} value={option}>{option}</option>
                                                    ))}
                                                </select>

                                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                                            </div>

                                            <FieldError id='demo-portfolio-error' message={errors.portfolioSize} />
                                        </div>

                                        <fieldset className='sm:col-span-2'>
                                            <legend className='text-sm font-medium text-foreground'>
                                                What would you like to improve? <span className='font-normal text-muted-foreground'>(optional)</span>
                                            </legend>

                                            <div className='mt-2 flex flex-wrap gap-2'>
                                                {interestOptions.map((interest, index) => {
                                                    const checked = form.interests.includes(interest)

                                                    return (
                                                        <label key={interest} className='cursor-pointer'>
                                                            <input
                                                                type='checkbox'
                                                                data-field={index === 0 ? 'interests' : undefined}
                                                                checked={checked}
                                                                onChange={() => toggleInterest(interest)}
                                                                className='peer sr-only'
                                                            />

                                                            <span className='inline-flex h-9 items-center gap-1.5 rounded-full bg-background px-3.5 text-sm text-foreground ring-1 ring-black/10 transition-colors peer-checked:bg-accent peer-checked:text-accent-foreground peer-checked:ring-primary/40 peer-focus-visible:ring-2 peer-focus-visible:ring-ring'>
                                                                {checked && <Check className='size-3.5' aria-hidden='true' />}
                                                                {interest}
                                                            </span>
                                                        </label>
                                                    )
                                                })}
                                            </div>

                                            <FieldError id='demo-interests-error' message={errors.interests} />
                                        </fieldset>

                                        <div className='sm:col-span-2'>
                                            <label htmlFor='demo-message' className='text-sm font-medium text-foreground'>
                                                Anything we should know? <span className='font-normal text-muted-foreground'>(optional)</span>
                                            </label>

                                            <textarea
                                                id='demo-message'
                                                data-field='message'
                                                rows={4}
                                                maxLength={demoRequestLimits.message}
                                                value={form.message}
                                                onChange={(event) => update('message', event.target.value)}
                                                aria-invalid={Boolean(errors.message)}
                                                aria-describedby={errors.message ? 'demo-message-error' : undefined}
                                                placeholder='Sites, markets you trade in, or the problem keeping you up at night.'
                                                className={cn(inputClasses, 'mt-1.5 h-auto resize-y py-3 leading-relaxed')}
                                            />

                                            <FieldError id='demo-message-error' message={errors.message} />
                                        </div>
                                    </div>

                                    <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                        <label htmlFor='demo-website'>Website</label>
                                        <input ref={honeypotRef} id='demo-website' name='website' type='text' tabIndex={-1} autoComplete='off' defaultValue='' />
                                    </div>

                                    {status === 'error' && serverMessage && (
                                        <div role='alert' className='mt-5 flex items-start gap-2.5 rounded-xl bg-orange-50 p-3.5 text-sm text-orange-800 ring-1 ring-orange-200'>
                                            <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                                            {serverMessage}
                                        </div>
                                    )}

                                    <div className='mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                                        <p className='text-xs leading-relaxed text-muted-foreground sm:max-w-[16rem]'>
                                            We only use your details to arrange your demo. Read our{' '}
                                            <Link href='/privacy' className='font-medium text-foreground underline underline-offset-2 hover:text-primary'>
                                                privacy policy
                                            </Link>
                                            .
                                        </p>

                                        <button
                                            type='submit'
                                            disabled={status === 'submitting'}
                                            className={buttonClasses({ size: 'lg', className: 'w-full sm:w-auto' })}
                                        >
                                            {status === 'submitting' ? (
                                                <>
                                                    <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                                                    Sending request
                                                </>
                                            ) : (
                                                <>
                                                    Request a demo
                                                    <ArrowRight className='size-4' aria-hidden='true' />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default DemoRequestSection
